import {
  ScreenSharePresets,
  VideoPreset,
  type LocalVideoTrack,
  type ScreenShareCaptureOptions,
  type TrackPublishOptions,
  type VideoCodec,
} from 'livekit-client';

/**
 * O coracao da qualidade.
 *
 * Duas decisoes que definem se jogo em movimento fica fluido ou vira borrao:
 *
 * 1) contentHint = 'motion'. Sem isso o Chrome assume que "tela" e texto parado,
 *    prioriza nitidez sobre taxa de quadros e cada giro de camera vira uma papa.
 *    Para codigo/planilha o certo e o oposto: 'detail'.
 *
 * 2) Codec H.264 por padrao. VP9/AV1 rendem mais qualidade por bit, mas codificam
 *    em software e roubam CPU de quem esta JOGANDO. H.264 cai no encoder de
 *    hardware (NVENC/QuickSync) e sai praticamente de graca.
 */

export type QualityId = 'alta' | 'media' | 'baixa';

export interface Quality {
  id: QualityId;
  label: string;
  width: number;
  height: number;
  frameRate: number;
  maxBitrate: number;
  contentHint: 'motion' | 'detail';
  codec: VideoCodec;
}

export const QUALITIES: Quality[] = [
  {
    id: 'alta',
    label: '1080p60',
    width: 1920,
    height: 1080,
    frameRate: 60,
    maxBitrate: 8_000_000,
    contentHint: 'motion',
    codec: 'h264',
  },
  {
    id: 'media',
    label: '1080p30',
    width: 1920,
    height: 1080,
    frameRate: 30,
    maxBitrate: 4_000_000,
    contentHint: 'motion',
    codec: 'h264',
  },
  {
    id: 'baixa',
    label: '720p30',
    width: 1280,
    height: 720,
    frameRate: 30,
    maxBitrate: 2_000_000,
    contentHint: 'motion',
    codec: 'h264',
  },
];

export const DEFAULT_QUALITY: QualityId = 'alta';

export function getQuality(id: QualityId): Quality {
  return QUALITIES.find((q) => q.id === id) ?? QUALITIES[0];
}

export function captureOptions(q: Quality): ScreenShareCaptureOptions {
  return {
    /*
     * Audio: sim para a aba, nao para o sistema inteiro.
     *
     * `audio: true` permite levar o som junto — util para mostrar um video ou
     * uma musica. Mas `systemAudio: 'exclude'` e deliberado, e a razao e a
     * propria documentacao do Chrome: o audio do sistema inclui a voz dos
     * outros participantes saindo do seu fone. Capturar isso mandaria a voz
     * dos seus amigos de volta para eles, com atraso.
     *
     * Como a conversa fica no Discord, isso seria eco garantido. Com 'exclude',
     * compartilhar uma ABA ainda oferece o som daquela aba (o caso do video),
     * mas compartilhar a tela inteira nao arrasta o Discord junto.
     */
    audio: true,
    systemAudio: 'exclude',
    resolution: { width: q.width, height: q.height, frameRate: q.frameRate },
    contentHint: q.contentHint,
    // Nao oferece a propria aba do app na lista (evita efeito tunel).
    selfBrowserSurface: 'exclude',
    // Deixa trocar de janela sem reabrir o seletor.
    surfaceSwitching: 'include',
  };
}

export function publishOptions(q: Quality): TrackPublishOptions {
  // VP9/AV1 usam SVC no LiveKit; simulcast classico so faz sentido em H.264/VP8.
  const useSimulcast = q.codec === 'h264' || q.codec === 'vp8';

  return {
    videoCodec: q.codec,
    // Para tela, o LiveKit le screenShareEncoding — nao videoEncoding.
    screenShareEncoding: {
      maxBitrate: q.maxBitrate,
      maxFramerate: q.frameRate,
      priority: 'high',
    },
    simulcast: useSimulcast,
    // Camada de resgate para quem esta com internet ruim assistindo.
    screenShareSimulcastLayers: useSimulcast
      ? [
          new VideoPreset(1280, 720, 1_500_000, Math.min(30, q.frameRate), 'medium'),
          ScreenSharePresets.h360fps15,
        ]
      : undefined,
    // Em movimento, preferimos perder resolucao a perder fluidez.
    degradationPreference:
      q.contentHint === 'motion' ? 'maintain-framerate' : 'maintain-resolution',
  };
}

/**
 * O SDK ja aplica o contentHint das captureOptions, mas navegadores as vezes
 * resetam a dica ao trocar de superficie (o botao "compartilhar outra janela").
 * Reforcar na MediaStreamTrack e barato e evita regressao silenciosa.
 */
export function enforceContentHint(track: LocalVideoTrack, q: Quality): void {
  const mst = track.mediaStreamTrack as MediaStreamTrack & { contentHint?: string };
  if (mst.contentHint !== q.contentHint) {
    mst.contentHint = q.contentHint;
  }
}
